import i18n from "../../constants/i18n";
import React from "react";
import { useUpdateIlome } from "./hooks/useUpdateIlome";
import styles from "./Ilome.module.scss";
import { useIsCeremonyMaster } from "../../core/hooks/useIsCeremonyMaster";
import { useHasOneOfRoles } from "../../core/hooks/useHasOneOfRoles";
import roles from "../../constants/roles";

interface IlomeContainerProps {
    hideModal: () => void;
    isVisible: boolean;
    ilomeName: string;
    ilomeEmail: string;
    ceremonyId: number;
    onSuccessfulUpdate: () => void;
}

export function IlomeContainer(props: IlomeContainerProps) {
    const update = useUpdateIlome(props.ceremonyId);
    const [ilomeName, setIlomeName] = React.useState(props.ilomeName);
    const [ilomeNameError, setIlomeNameError] = React.useState("");
    const [ilomeEmail, setIlomeEmail] = React.useState(props.ilomeEmail);
    const [ilomeEmailError, setIlomeEmailError] = React.useState("");

    const isCeremonyMaster = useIsCeremonyMaster(props.ceremonyId);
    const canEditIlome =
        useHasOneOfRoles([roles.admin, roles.crematory]) || isCeremonyMaster;
    const disabled = !(!props.ilomeName || !props.ilomeEmail || canEditIlome);

    function onSubmit() {
        if (!isValidEmail(ilomeEmail)) {
            setIlomeEmailError(i18n.ilomeModal.invalidEmailError);
            return;
        } else {
            setIlomeEmailError("");
        }

        if (!isValidIlomeName(ilomeName)) {
            setIlomeNameError(i18n.ilomeModal.invalidNameError);
            return;
        } else {
            setIlomeNameError("");
        }

        update.mutate(
            {
                ilomeName: ilomeName,
                ilomeEmail: ilomeEmail,
            },
            {
                onSuccess: () => {
                    props.onSuccessfulUpdate();
                },
            }
        );
    }

    function isValidEmail(email: string) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    function isValidIlomeName(name: string): boolean {
        return typeof name === "string" && name.trim().length > 0;
    }

    return (
        <>
            <header className={`modal-card-head ${styles.modalCardHead}`}>
                <h2
                    className={`modal-card-title has-text-primary ${styles.modalCardTitle}`}
                >
                    <img
                        src="/ilome-logo.png"
                        alt={i18n.timelineOverview.ilomeLabel}
                        className={`${styles.ilomeLogo}`}
                    />{" "}
                    {i18n.ilomeModal.title}
                </h2>
                <button
                    onClick={props.hideModal}
                    className="delete"
                    aria-label="close"
                />
            </header>
            <section className="modal-card-body">
                <div className={`${styles.helpText}`}>
                    <p>
                        {i18n.ilomeModal.explanation1}
                        <br />
                        <br />
                    </p>
                    <p>
                        {i18n.ilomeModal.explanation2}
                        <br />
                        <br />
                    </p>
                    <p>
                        {disabled
                            ? i18n.ilomeModal.nameAndEmailAllreadyFilledIn
                            : i18n.ilomeModal.explanation3}
                    </p>
                </div>
                <div className={`content ${styles.formContainer}`}>
                    {" "}
                    <div className="field">
                        <label className="label">
                            {i18n.ilomeModal.ilomeNameLabel}
                        </label>
                        <input
                            name="ilomeName"
                            value={ilomeName}
                            className="input is-rounded"
                            placeholder={i18n.ilomeModal.ilomeNamePlaceholder}
                            onChange={(e) => setIlomeName(e.target.value)}
                            disabled={disabled}
                        />
                        {ilomeNameError && (
                            <p className="help is-danger">{ilomeNameError}</p>
                        )}
                    </div>
                    <div className="field">
                        <label className="label">
                            {i18n.ilomeModal.ilomeEmailLabel}
                        </label>
                        <input
                            name="ilomeEmail"
                            value={ilomeEmail}
                            className="input is-rounded"
                            placeholder={i18n.ilomeModal.ilomeEmailPlaceholder}
                            onChange={(e) => setIlomeEmail(e.target.value)}
                            disabled={disabled}
                        />
                        {ilomeEmailError && (
                            <p className="help is-danger">{ilomeEmailError}</p>
                        )}
                    </div>
                </div>
            </section>
            <footer className={`modal-card-foot ${styles.modalCardFooter}`}>
                {!disabled && (
                    <button
                        onClick={onSubmit}
                        className={`button is-primary is-rounded ${styles.submitButton}`}
                    >
                        {i18n.ilomeModal.submit}
                    </button>
                )}
            </footer>
        </>
    );
}
